import { useMemo } from 'react';
import { validateTenantSlug } from '../../../lib/tenant-slug.js';

/**
 * Step 2 of the tenant provisioning wizard (D1.5.3).
 *
 * Per TENANT-PROVISIONING-DESIGN-2026-04-20.md §6/§8: v1 auto-provisions
 * two subdomains from the tenant's slug and does not yet accept a custom
 * (CNAME) hostname. The custom-domain toggle renders as a clearly-disabled
 * control so admins see where it lives, with a note that the custom-domain
 * flow (DNS + verification poller) ships in a later workstream.
 *
 * Subdomains shown live-derive from the draft's `slug` (written by Step 1).
 * If the slug is empty or fails shape validation, the step prompts the
 * admin to return to Step 1 rather than rendering a malformed preview.
 */

// Wildcard DNS / Vercel cert already points these at the platform; no
// per-tenant DNS or SSL action required for subdomain provisioning.
//
// Per ADR-2026-04-28-control-plane-separation: customer surface flattens
// from `<slug>.app.animalloversapp.com` to `<slug>.animalloversapp.com`.
// `app.` was a holdover from the prior "ALA = Tenant 1" model. Admin
// surface (`<slug>.admin.animalloversapp.com`) keeps its prefix to preserve
// cookie scoping and security-posture separation between customer and staff
// dashboards. The bare `admin.animalloversapp.com` (no slug) is reserved
// for the Ferro/IAID platform-operator console.
export const ROOT_CUSTOMER_HOST = 'animalloversapp.com';
export const ROOT_ADMIN_HOST = 'admin.animalloversapp.com';

export function customerSubdomain(slug: string): string {
  return `${slug}.${ROOT_CUSTOMER_HOST}`;
}

export function adminSubdomain(slug: string): string {
  return `${slug}.${ROOT_ADMIN_HOST}`;
}

type StepDomainProps = {
  payload: Record<string, unknown>;
};

export function StepDomain({ payload }: StepDomainProps): JSX.Element {
  const slug = typeof payload.slug === 'string' ? payload.slug.trim() : '';
  const slugShapeOk = useMemo(() => validateTenantSlug(slug).ok, [slug]);

  return (
    <div className="ferro-form" aria-label="Domain binding — Step 2">
      <section className="ferro-field" aria-labelledby="step-domain-subdomains">
        <h2 id="step-domain-subdomains" className="ferro-field__label">
          Auto-provisioned subdomains
        </h2>
        <p className="ferro-muted">
          These resolve as soon as the tenant is created — the platform's wildcard
          DNS and SSL already cover both hosts, no admin action needed.
        </p>

        {slugShapeOk ? (
          <ul className="ferro-wizard__subdomains" aria-label="Subdomain preview">
            <li>
              <span className="ferro-field__label">Customer surface</span>
              <code data-testid="step-domain-customer-host">
                {customerSubdomain(slug)}
              </code>
            </li>
            <li>
              <span className="ferro-field__label">Staff surface</span>
              <code data-testid="step-domain-admin-host">
                {adminSubdomain(slug)}
              </code>
            </li>
          </ul>
        ) : (
          <p role="alert" className="ferro-form__error">
            Finish Step 1 with a valid slug to preview the subdomains.
          </p>
        )}
      </section>

      <section className="ferro-field" aria-labelledby="step-domain-custom">
        <h2 id="step-domain-custom" className="ferro-field__label">
          Custom domain
        </h2>
        <label
          className="ferro-radio"
          style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}
        >
          <input
            type="checkbox"
            name="addCustomDomain"
            checked={false}
            disabled
            readOnly
            aria-describedby="step-domain-custom-note"
            data-testid="step-domain-custom-toggle"
          />
          <span>
            <strong>Add custom domain</strong>{' '}
            <span className="ferro-muted">
              — white-label CNAME (e.g. <code>app.happytailhappydog.com</code>)
            </span>
          </span>
        </label>
        <p id="step-domain-custom-note" className="ferro-muted">
          Available in a later release. Subdomain-only tenants ship today;
          CNAME verification + Let's Encrypt issuance land with the domain
          poller.
        </p>
      </section>
    </div>
  );
}
